import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BLUE = { r: 65, g: 105, b: 225 };
const STAR_WHITE = { r: 235, g: 245, b: 255 };
const COUNT = 140;
const BURST_FRAMES = 62;
const SETTLE_FRAMES = 130;

function ParticleBackground() {
  const canvasRef = useRef(null);
  const burstRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    burstRef.current = true;
  }, [location.pathname]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let pts = [];
    let raf;
    let burstProgress = 0;
    let settleGlow = 0;
    let postTransitionShowers = 0;
    const meteors = [];
    let meteorTimer = null;
    const showerTimers = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      pts = Array.from({ length: COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 1.7 + 0.45,
        tw: Math.random() * Math.PI * 2,
        sp: Math.random() * 0.026 + 0.009,
        baseAlpha: Math.random() * 0.34 + 0.14,
        warm: Math.random() > 0.7,
        bvx: 0,
        bvy: 0,
        glow: 0,
        glowDecay: 0.018,
      }));
    };

    function spawnMeteor(isComet = false, scheduleNext = true) {
      const W = canvas.width;
      const H = canvas.height;
      const startEdge = Math.random() < 0.65 ? 'top' : 'left';
      let x, y;
      if (startEdge === 'top') {
        x = Math.random() * W * 0.9;
        y = -40;
      } else {
        x = -40;
        y = Math.random() * H * 0.6;
      }
      const angle = Math.random() * 0.45 + 0.35;
      const speed = isComet ? Math.random() * 12 + 18 : Math.random() * 10 + 9;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      const life = isComet ? 42 : 54;
      meteors.push({
        x,
        y,
        vx,
        vy,
        len: isComet ? Math.random() * 8 + 10 : Math.random() * 6 + 6,
        life,
        maxLife: life,
        alpha: 0.95,
        width: isComet ? Math.random() * 2.4 + 2.2 : Math.random() * 1.5 + 1.1,
        blueTail: Math.random() > 0.35,
      });
      if (scheduleNext) {
        const next = Math.random() * 5500 + 2400;
        meteorTimer = setTimeout(spawnMeteor, next);
      }
    }

    function spawnCometShower() {
      postTransitionShowers = 2;
      spawnMeteor(true, false);
      showerTimers.push(setTimeout(() => spawnMeteor(true, false), 260));
    }

    const loop = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      if (burstRef.current) {
        burstRef.current = false;
        burstProgress = BURST_FRAMES;
        settleGlow = SETTLE_FRAMES;
        pts.forEach((p) => {
          const angle = Math.random() * Math.PI * 2;
          const speed = Math.random() * 2.1 + 0.55;
          p.bvx = Math.cos(angle) * speed;
          p.bvy = Math.sin(angle) * speed;
          p.glow = Math.max(p.glow, Math.random() * 0.35 + 0.28);
        });
      }

      const bursting = burstProgress > 0;
      if (bursting) {
        burstProgress--;
        if (burstProgress === 0) spawnCometShower();
      }
      if (settleGlow > 0) settleGlow--;

      if (postTransitionShowers > 0 && Math.random() < 0.028) {
        postTransitionShowers--;
        spawnMeteor(true, false);
      }

      if (Math.random() < 0.018) {
        const rp = pts[Math.floor(Math.random() * pts.length)];
        rp.glow = 1.0;
        rp.glowDecay = 0.016 + Math.random() * 0.026;
      }

      pts.forEach((p) => {
        p.tw += p.sp * 2;
        const twink = 0.5 + 0.5 * Math.sin(p.tw);

        const driftStrength = 0.02;
        p.vx += (Math.random() - 0.5) * driftStrength;
        p.vy += (Math.random() - 0.5) * driftStrength;

        if (bursting) {
          const decay = burstProgress / 90;
          p.vx += p.bvx * decay * 0.04;
          p.vy += p.bvy * decay * 0.04;
        }

        p.vx *= 0.98;
        p.vy *= 0.98;
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        if (p.y < -10) p.y = H + 10;
        if (p.y > H + 10) p.y = -10;

        const burstGlow = bursting ? (burstProgress / BURST_FRAMES) * 0.26 : 0;
        const afterGlow = settleGlow > 0 ? (settleGlow / SETTLE_FRAMES) * 0.2 : 0;
        const glow = p.glow || 0;
        if (glow > 0) p.glow = Math.max(0, glow - p.glowDecay);
        const sparkle = twink > 0.82 ? (twink - 0.82) * 1.9 : 0;
        const alpha = p.baseAlpha * (0.55 + 0.65 * twink) + burstGlow + afterGlow + glow * 0.9;
        const size = p.size * (1 + 0.35 * twink + burstGlow * 0.8 + afterGlow + glow * 1.25);
        const color = p.warm && twink > 0.7 ? STAR_WHITE : BLUE;

        if (burstGlow > 0.08 || glow > 0.12 || sparkle > 0.05) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * (3.2 + glow * 2.5), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${color.r},${color.g},${color.b},${Math.min(0.18, burstGlow * 0.12 + glow * 0.16 + sparkle * 0.18)})`;
          ctx.fill();
        }

        if (sparkle > 0.08) {
          ctx.strokeStyle = `rgba(${STAR_WHITE.r},${STAR_WHITE.g},${STAR_WHITE.b},${sparkle * 0.22})`;
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.moveTo(p.x - size * 2.8, p.y);
          ctx.lineTo(p.x + size * 2.8, p.y);
          ctx.moveTo(p.x, p.y - size * 2.8);
          ctx.lineTo(p.x, p.y + size * 2.8);
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color.r},${color.g},${color.b},${Math.min(alpha, 0.95)})`;
        ctx.fill();
      });

      for (let i = meteors.length - 1; i >= 0; i--) {
        const m = meteors[i];
        m.x += m.vx;
        m.y += m.vy;
        m.life -= 1;
        m.alpha = Math.max(0, Math.min(0.95, m.life / m.maxLife));
        const tailX = m.x - m.vx * m.len;
        const tailY = m.y - m.vy * m.len;
        const grad = ctx.createLinearGradient(m.x, m.y, tailX, tailY);
        grad.addColorStop(0, `rgba(255,255,255,${m.alpha})`);
        grad.addColorStop(0.2, `rgba(185,210,255,${m.alpha * 0.55})`);
        if (m.blueTail) grad.addColorStop(0.55, `rgba(65,105,225,${m.alpha * 0.22})`);
        grad.addColorStop(1, `rgba(255,255,255,0)`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = m.width;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(m.x, m.y);
        ctx.lineTo(tailX, tailY);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(m.x, m.y, m.width * 1.9, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${Math.min(1, m.alpha + 0.1)})`;
        ctx.fill();

        if (m.life <= 0 || m.x > W + 260 || m.y > H + 260) meteors.splice(i, 1);
      }

      raf = requestAnimationFrame(loop);
    };

    resize();
    window.addEventListener('resize', resize);
    loop();
    meteorTimer = setTimeout(spawnMeteor, Math.random() * 2200 + 1400);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      if (meteorTimer) clearTimeout(meteorTimer);
      showerTimers.forEach(clearTimeout);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: -1,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

export default ParticleBackground;
